import { hexToRgb, ChartJS, EmptyBox } from '@blockanalitica/ui';
import { useMemo, ReactNode } from 'react';
import { useTheme } from 'styled-components';
import type {
  ChartDataset,
  ChartConfig
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import type { ChartType, ScriptableContext } from 'chart.js';

import _ from 'lodash';

export type SimpleChartData = {
  [field: string]: number | string | null;
};

export type SimpleChartDataset = {
  xField: string;
  yField: string;
  data?: SimpleChartData[];
  label?: string;
  borderColor?: string;
  backgroundColor?: string | string[];
};

export interface JustLineChartProps {
  data?: SimpleChartData[];
  datasets: SimpleChartDataset[];
  config?: ChartConfig;
  emptyComponent?: ReactNode;
}

export default function JustLineChart({
  data,
  datasets,
  config,
  emptyComponent = <EmptyBox />,
  ...rest
}: JustLineChartProps) {
  const theme = useTheme();

  const chartDatasets: ChartDataset[] = useMemo(() => {
    if (datasets.length) {
      return datasets.map((ds) => {
        const color = ds.borderColor || (theme.colors.chartLine as string);
        const datasetData = ds.data ?? data;

        if (datasetData && !_.isEmpty(datasetData)) {
          const dataMapped = datasetData.map((item) => ({
            x: item[ds.xField || 'date'],
            y: item[ds.yField || 'total']
          }));

          return {
            label: ds.label,
            data: dataMapped,
            borderColor: color,
            backgroundColor: ds.backgroundColor
              ? ds.backgroundColor
              : function (context) {
                  const chart = context.chart;
                  const { ctx, chartArea } = chart;

                  if (!chartArea) {
                    // This case happens on initial chart load
                    return null;
                  }
                  const gradient = ctx.createLinearGradient(
                    0,
                    chartArea.bottom,
                    0,
                    chartArea.top
                  );

                  const hexColor = hexToRgb(color);
                  gradient.addColorStop(
                    0,
                    `rgba(${hexColor.r}, ${hexColor.g}, ${hexColor.b}, 0)`
                  );
                  gradient.addColorStop(
                    1,
                    `rgba(${hexColor.r}, ${hexColor.g}, ${hexColor.b}, .18)`
                  );

                  return gradient;
                }
          };
        }

        return {};
      });
    }

    return [];
  }, [data, datasets, theme.colors.chartLine]);

  const chartConfig: ChartConfig = useMemo(() => {
    const cData = chartDatasets.every((v) => _.isEmpty(v))
      ? null
      : {
          datasets: chartDatasets
        };

    const defaultConfig = {
      type: 'line',
      data: cData,
      options: {
        aspectRatio: 2.5,
        fill: true,
        animation: false,
        cubicInterpolationMode: 'monotone',
        responsive: true,
        interaction: {
          intersect: false,
          mode: 'nearest',
          axis: 'x'
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 0
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            display: false,
            usePointStyle: true,
            pointStyle: 'circle',
            callbacks: {
              labelColor: function (context: ScriptableContext<ChartType>) {
                const border = context.dataset.borderColor;
                const color = Array.isArray(border)
                  ? border[context.dataIndex]
                  : border;

                return {
                  borderColor: color,
                  backgroundColor: color
                };
              }
            }
          }
        },
        scales: {
          x: {
            display: false,
            type: 'time',
            time: {
              unit: 'day',
              tooltipFormat: 'MMM d',
              displayFormats: {
                day: 'MMM d',
                week: 'MMM d',
                month: 'MMM yyyy'
              }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            display: false,
            type: 'linear',
            ticks: {
              color: theme.colors.muted,
              format: { notation: 'compact' }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        }
      }
    };

    const mergedConfig = _.merge(defaultConfig, config);
    return mergedConfig;
  }, [chartDatasets, config, theme.colors.muted]);

  return (
    <>{chartConfig.data ? <ChartJS config={chartConfig} /> : emptyComponent}</>
  );
}
